import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaBox } from "react-icons/fa";
import Header from "../Header";
import { UserContext } from "../UserContext";
import apiClient from "../../api/client";
import { accountOverview } from "../../api/accountsApi";
import { getErrorMessage } from "../../utils/network";
import OperatorDevicesPanel from "../NmsDevices/OperatorDevicesPanel";
import AccountEditModal from "./AccountEditModal";
import WalletModal from "./WalletModal";
import "./index.css";

const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/**
 * One admin / operator on its own page (route /accounts/:id): wallet, customer counts, details
 * and every action that used to live in the pop-up on the Admins / Operators list. After an
 * edit, a top-up, a debit or a plan assignment you come back here, not to the full list.
 */
const AccountPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, tempLogin } = useContext(UserContext);
  const isSuperAdmin = user?.role === "superadmin";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [walletMode, setWalletMode] = useState(null); // "credit" | "debit"
  const [devices, setDevices] = useState(false);
  const [statusConfirm, setStatusConfirm] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await accountOverview(id);
      if (d.status !== "success") throw new Error(d.error || "Failed to load");
      setData(d);
      setError("");
    } catch (err) {
      setError(getErrorMessage(err, "Could not load this account"));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const acc = data?.account;
  const stats = data?.stats || {};
  const role = String(acc?.role || "").toLowerCase();
  const listPath = role === "admin" ? "/admins" : "/operators";
  const enabled = Number(acc?.operatorEnabled) === 1;
  const isSelf = acc && Number(acc.id) === Number(user?.operatorId);
  // Same rules as the server: super admin -> any admin / operator; admin -> its own operators.
  const canManage = !!acc && !isSelf && (isSuperAdmin
    ? role === "admin" || role === "operator"
    : user?.role === "admin" && role === "operator" && Number(acc.created_by) === Number(user.operatorId));

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(listPath);
  };

  const toggleStatus = async () => {
    setStatusSaving(true);
    try {
      const { data: d } = await apiClient.post("/toggle_operator_status.php", { id: acc.id, enabled: enabled ? 0 : 1 });
      if (!d.success) throw new Error(d.message);
      toast.success(d.message || (enabled ? "Account disabled" : "Account enabled"));
      setStatusConfirm(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not update the account status"));
    } finally {
      setStatusSaving(false);
    }
  };

  const loginAs = async () => {
    let res;
    try {
      res = await apiClient.post("/impersonate.php", { targetUserId: acc.id }, { validateStatus: () => true });
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not log in as this account"));
      return;
    }
    const d = res.data;
    if (res.status >= 200 && res.status < 300 && d.token) {
      tempLogin(d.token);
      window.location.href = d.role === "admin" ? "/admin" : "/operator";
    } else {
      toast.warn("Impersonation failed: " + (d.error || "Unknown error"));
    }
  };

  const mappingLinks = acc
    ? [
        { to: `/assign-plans/${acc.id}`, label: "OTT Packages", show: true },
        { to: `/assign-internet-plans/${acc.id}`, label: "Internet Plans", show: Number(acc.internetEnabled) === 1 },
        { to: `/assign-dvpl-plans/${acc.id}`, label: "DVPL Plans", show: Number(acc.dvplEnabled) === 1 },
        { to: `/assign-railtel-plans/${acc.id}`, label: "RailTel Plans", show: Number(acc.railtelEnabled) === 1 },
        { to: `/assign-pioneeriptv-plans/${acc.id}`, label: "Pioneer IPTV Plans", show: true },
        { to: `/assign-combos/${acc.id}`, label: "Combos", show: true },
        { to: `/assign-kyc-providers/${acc.id}`, label: "KYC Providers", show: Number(acc.internetEnabled) === 1 },
        {
          to: `/assign-pioneeriptv-branches/${acc.id}`,
          label: "Pioneer Branches",
          show: acc.pioneeriptvToken !== null && acc.pioneeriptvToken !== undefined && acc.pioneeriptvToken !== "",
        },
      ].filter((l) => l.show)
    : [];

  return (
    <>
      <Header />
      <div className="acc-page">
        <button type="button" className="acc-back" onClick={goBack}>← Back to {role === "admin" ? "Admins" : "Operators"}</button>

        {loading ? (
          <div className="acc-card acc-empty">Loading account…</div>
        ) : error ? (
          <div className="acc-card acc-empty">
            <p>{error}</p>
            <button type="button" className="acc-btn" onClick={() => { setLoading(true); load(); }}>Try again</button>
          </div>
        ) : (
          <>
            <div className="acc-card acc-head">
              <div className="acc-avatar">{(acc.operatorName || acc.username || "?").charAt(0).toUpperCase()}</div>
              <div className="acc-title">
                <h1>{acc.operatorName || "No name"}</h1>
                <p>@{acc.username} · <span className="acc-role">{acc.role}</span>{acc.created_by_name ? ` · created by ${acc.created_by_name}` : ""}</p>
              </div>
              {canManage ? (
                <button type="button" className={`acc-status ${enabled ? "is-on" : "is-off"}`} onClick={() => setStatusConfirm(true)}
                  title={enabled ? "Click to disable this account" : "Click to enable this account"}>
                  <span className="acc-status__dot" />{enabled ? "Enabled" : "Disabled"}<span className="acc-status__hint">Change</span>
                </button>
              ) : (
                <span className={`acc-status acc-status--static ${enabled ? "is-on" : "is-off"}`}>
                  <span className="acc-status__dot" />{enabled ? "Enabled" : "Disabled"}
                </span>
              )}
            </div>

            <div className="acc-stats">
              <div className="acc-stat acc-stat--wallet">
                <span>Wallet</span>
                <strong>{rupees(acc.wallet)}</strong>
                {canManage && (
                  <div className="acc-stat__btns">
                    <button type="button" className="acc-btn acc-btn--sm acc-btn--primary" onClick={() => setWalletMode("credit")}>+ Add balance</button>
                    <button type="button" className="acc-btn acc-btn--sm acc-btn--danger-outline" onClick={() => setWalletMode("debit")}>− Debit</button>
                  </div>
                )}
              </div>
              <div className="acc-stat">
                <span>Customers</span>
                <strong>{Number(stats.customers || 0).toLocaleString("en-IN")}</strong>
                <small>directly under this account</small>
              </div>
              {role === "admin" && (
                <>
                  <div className="acc-stat">
                    <span>Operators</span>
                    <strong>{Number(stats.operators || 0).toLocaleString("en-IN")}</strong>
                    <small>created by this admin</small>
                  </div>
                  <div className="acc-stat">
                    <span>Operators' customers</span>
                    <strong>{Number(stats.operator_customers || 0).toLocaleString("en-IN")}</strong>
                    <small>across its operators</small>
                  </div>
                </>
              )}
              {stats.internet_accounts !== undefined && (
                <div className="acc-stat">
                  <span>Internet accounts</span>
                  <strong>{Number(stats.internet_accounts || 0).toLocaleString("en-IN")}</strong>
                  <small>Internet / DVPL linked</small>
                </div>
              )}
            </div>

            <div className="acc-cols">
              <div className="acc-card">
                <div className="acc-card__head">
                  <h2>Details</h2>
                  {canManage && <button type="button" className="acc-btn acc-btn--sm" onClick={() => setEditing(true)}>Edit profile</button>}
                </div>
                <dl className="acc-dl">
                  <div><dt>Network name</dt><dd>{acc.networkName || "—"}</dd></div>
                  <div><dt>Phone</dt><dd>{acc.phoneNumber ? <a href={`tel:${acc.phoneNumber}`}>{acc.phoneNumber}</a> : "—"}</dd></div>
                  <div><dt>Email</dt><dd>{acc.Email || "—"}</dd></div>
                  <div><dt>Channel mode</dt><dd>{acc.channel_mode || "default"}</dd></div>
                  <div className="acc-dl__full"><dt>Address</dt><dd>{acc.address || "—"}</dd></div>
                  <div className="acc-dl__full">
                    <dt>Services</dt>
                    <dd className="acc-badges">
                      {[["Internet", acc.internetEnabled], ["DVPL", acc.dvplEnabled], ["RailTel", acc.railtelEnabled]].map(([n, v]) => (
                        <span key={n} className={`acc-badge ${Number(v) === 1 ? "is-on" : ""}`}>{n} {Number(v) === 1 ? "on" : "off"}</span>
                      ))}
                      {(Number(acc.internetEnabled) === 1 || Number(acc.dvplEnabled) === 1) && (
                        <span className={`acc-badge ${acc.aadhaarRequired === undefined || Number(acc.aadhaarRequired) === 1 ? "is-on" : ""}`}>
                          Aadhaar {acc.aadhaarRequired === undefined || Number(acc.aadhaarRequired) === 1 ? "required" : "optional"} for recharge
                        </span>
                      )}
                      {Number(acc.internetEnabled) === 1 && (acc.internetPartnerId || acc.internetBranchId) ? (
                        <span className="acc-badge">Internet partner #{acc.internetPartnerId || "—"} · branch #{acc.internetBranchId || "—"}</span>
                      ) : null}
                      {Number(acc.dvplEnabled) === 1 && (acc.dvplPartnerId || acc.dvplBranchId) ? (
                        <span className="acc-badge">DVPL partner #{acc.dvplPartnerId || "—"} · branch #{acc.dvplBranchId || "—"}</span>
                      ) : null}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="acc-card">
                <div className="acc-card__head"><h2>Actions</h2></div>
                <div className="acc-actions">
                  {canManage && <button type="button" className="acc-btn" onClick={() => setEditing(true)}>Edit profile</button>}
                  {canManage && <button type="button" className="acc-btn" onClick={() => setWalletMode("credit")}>Add balance</button>}
                  {canManage && <button type="button" className="acc-btn acc-btn--danger-outline" onClick={() => setWalletMode("debit")}>Debit</button>}
                  {canManage && <button type="button" className="acc-btn" onClick={loginAs}>Login as {role === "admin" ? "Admin" : "Operator"}</button>}
                  <button type="button" className="acc-btn" disabled={!acc.phoneNumber} onClick={() => setDevices((v) => !v)}
                    title={acc.phoneNumber ? "OLTs and switches on the NMS" : "No phone number on this account"}>
                    {devices ? "Hide devices" : "Devices"}
                  </button>
                </div>
                {canManage && (
                  <>
                    <h3 className="acc-sub">Plans &amp; mapping</h3>
                    <div className="acc-map">
                      {mappingLinks.map((l) => (
                        <button key={l.to} type="button" onClick={() => navigate(l.to)}>
                          <span className="acc-map__icon"><FaBox aria-hidden="true" /></span>
                          <span className="acc-map__label">Assign {l.label}</span>
                          <span className="acc-map__go" aria-hidden="true">›</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {devices && (
              <div className="acc-card acc-devices">
                <OperatorDevicesPanel operator={acc} onBack={() => setDevices(false)} onModeChange={() => {}} />
              </div>
            )}

            <div className="acc-card">
              <div className="acc-card__head"><h2>Recent wallet entries</h2></div>
              {(data.wallet_recent || []).length === 0 ? (
                <p className="acc-hint">No wallet entries yet.</p>
              ) : (
                <div className="acc-table-wrap">
                  <table className="acc-table">
                    <thead><tr><th>Date</th><th>Type</th><th className="num">Amount</th><th className="num">Balance after</th><th>Remarks</th></tr></thead>
                    <tbody>
                      {data.wallet_recent.map((w, i) => (
                        <tr key={i}>
                          <td>{w.created_at}</td>
                          <td><span className={`acc-tx acc-tx--${w.transaction_type}`}>{w.transaction_type}</span></td>
                          <td className="num">{rupees(w.amount)}</td>
                          <td className="num">{w.balance_after !== null ? rupees(w.balance_after) : "—"}</td>
                          <td>{w.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Phones: one small card per entry instead of the table */}
                  <ul className="acc-wlist">
                    {data.wallet_recent.map((w, i) => (
                      <li key={i} className="acc-wlist__row">
                        <div className="acc-wlist__top">
                          <span className={`acc-tx acc-tx--${w.transaction_type}`}>{w.transaction_type}</span>
                          <strong className={`acc-wlist__amt acc-wlist__amt--${w.transaction_type}`}>
                            {w.transaction_type === "credit" ? "+" : "−"}{rupees(w.amount)}
                          </strong>
                        </div>
                        {w.remarks ? <div className="acc-wlist__remarks">{w.remarks}</div> : null}
                        <div className="acc-wlist__meta">
                          <span>{w.created_at}</span>
                          <span>Balance {w.balance_after !== null ? rupees(w.balance_after) : "—"}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {editing && acc && (
        <AccountEditModal account={acc} isSuperAdmin={isSuperAdmin} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}
      {walletMode && acc && (
        <WalletModal mode={walletMode} account={acc} user={user} onClose={() => setWalletMode(null)}
          onDone={() => { setWalletMode(null); load(); }} />
      )}
      {statusConfirm && acc && (
        <div className="acc-overlay" role="alertdialog" aria-modal="true">
          <div className="acc-modal acc-modal--narrow">
            <div className="acc-modal__head"><h3>{enabled ? "Disable" : "Enable"} {acc.operatorName || acc.username}?</h3></div>
            <div className="acc-modal__body">
              <p className="acc-hint">
                {enabled
                  ? `They will be logged out immediately and won't be able to sign in or recharge until enabled again.`
                  : `They will be able to sign in and use the portal again.`}
              </p>
            </div>
            <div className="acc-modal__foot">
              <button type="button" className="acc-btn" onClick={() => setStatusConfirm(false)} disabled={statusSaving}>Cancel</button>
              <button type="button" className={`acc-btn ${enabled ? "acc-btn--danger" : "acc-btn--primary"}`} onClick={toggleStatus} disabled={statusSaving}>
                {statusSaving ? "Saving…" : enabled ? "Yes, disable" : "Yes, enable"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountPage;
